import {
  Button,
  Select,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  useTheme,
  Checkbox,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { usePrevNextNavigationContext } from "components/PrevNextNavigation";
import { useThemeMode } from "hooks/useThemeMode";
import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation, useMatch } from "react-router-dom";
import { useBreadcrumb } from "../../../hooks/useBreadcrumbs";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { TierIiReportKind } from "generated-graphql/graphql";
import { StatsProvider, useStats } from "./StatsContext";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { OmnisearchProvider } from "providers/OmnisearchContext";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import {
  parseOmnisearch,
  stringifyOmnisearch,
} from "components/Omnisearch/FilterPopover";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { WorkflowTagsButton } from "./WorkflowTagsFilter";

function ReportTypePicker({
  reportKinds,
  setReportKind,
}: {
  reportKinds: TierIiReportKind[];
  setReportKind: (reportKind: TierIiReportKind[] | undefined) => void;
}) {
  const theme = useTheme();
  return (
    <FormControl sx={{ width: theme.spacing(20) }}>
      <InputLabel id="report-type" shrink>
        Report Type(s)
      </InputLabel>
      <Select
        variant="standard"
        sx={{
          "&.MuiInputBase-root": {
            marginTop: "0.375rem",
          },
        }}
        fullWidth
        multiple
        labelId="report-type"
        label="Report Type(s)"
        value={reportKinds}
        displayEmpty
        onChange={(event) =>
          setReportKind(event.target.value as TierIiReportKind[])
        }
        renderValue={(value) => {
          if (!value || value.length === 0) {
            return <span>All</span>;
          }
          return value.map((v) => prettyPrintEnumValue(v)).join(", ");
        }}
      >
        {Object.values(TierIiReportKind).map((reportKind) => (
          <MenuItem
            key={reportKind}
            value={reportKind}
            sx={{ paddingLeft: 1, paddingRight: 0, paddingY: 0 }}
          >
            <Checkbox checked={reportKinds.includes(reportKind)} />
            {prettyPrintEnumValue(reportKind)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function FulfillmentDashboardContent() {
  const { themeMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const facilitiesMatch = useMatch("/staff/fulfillment/facilities");
  const workflowsMatch = useMatch("/staff/fulfillment/workflows");
  const errorClusteringMatch = useMatch("/staff/fulfillment/error-clustering");
  const overviewMatch =
    facilitiesMatch || workflowsMatch || errorClusteringMatch;

  const { statsOpen, handleToggle } = useStats();

  const { omnisearch, getOmnisearchValue, setOmnisearch } = useOmnisearchInput({
    isURLDriven: true,
    initialFilters: [],
  });

  // Helper function to properly parse workflow tags from omnisearch
  const getWorkflowTagsFromOmnisearch = useCallback((): string[] => {
    if (!omnisearch) {
      return [];
    }

    // Use extractKeyValuePairs to properly handle quoted values with pipes
    const pairs = extractKeyValuePairs(omnisearch);
    const workflowTagsPair = pairs.find((p) => p.key === "tags");

    let tags = workflowTagsPair?.value ?? [];

    // If we got a single string with pipe separators, split it
    if (
      tags.length === 1 &&
      typeof tags[0] === "string" &&
      tags[0].includes("|")
    ) {
      tags = tags[0]
        .split("|")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
    }

    return tags;
  }, [omnisearch]);

  const { setNavigationKey } = usePrevNextNavigationContext();

  const getNewFilters = useCallback(
    (filters: Record<string, any>) => {
      const prev = parseOmnisearch(omnisearch);
      const reportKind = getOmnisearchValue("reportKind");
      const reportingYear = getOmnisearchValue("reportingYear");

      return {
        ...prev,
        reportKind,
        reportingYear,
        ...filters,
      };
    },
    [omnisearch, getOmnisearchValue]
  );

  // Determine current view based on the route
  const currentView = location.pathname.includes("/workflows")
    ? "workflow"
    : location.pathname.includes("/error-clustering")
    ? "error-clustering"
    : "facilities";

  const handleYearChange = useCallback(
    (value: string | number | null) => {
      const newFilters = getNewFilters({ reportingYear: value?.toString() });

      setOmnisearch(stringifyOmnisearch(newFilters));
    },
    [omnisearch, setOmnisearch]
  );

  const setReportKind = useCallback(
    (reportKinds: TierIiReportKind[] | undefined) => {
      const newFilters = getNewFilters({
        reportKind: reportKinds?.join("|"),
      });

      setOmnisearch(stringifyOmnisearch(newFilters));
    },
    [omnisearch, setOmnisearch]
  );

  const setWorkflowTags = useCallback(
    (workflowTags: string[]) => {
      const joinedTags =
        workflowTags.length > 0 ? workflowTags.join("|") : undefined;

      const newFilters = getNewFilters({
        tags: joinedTags,
      });

      const newOmnisearch = stringifyOmnisearch(newFilters);

      setOmnisearch(newOmnisearch);
    },
    [getNewFilters, setOmnisearch]
  );

  useEffect(() => {
    // Set the navigation key when the component mounts
    setNavigationKey("/staff/fulfillment");

    // Reset the navigation key when the component unmounts
    return () => {
      setNavigationKey("");
    };
  }, [setNavigationKey]);

  useBreadcrumb({
    label: "Fulfillment",
    to: "/staff/fulfillment",
  });

  const handleViewChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newView: string | null) => {
      // Only preserve reportingYear and reportKind when navigating between views
      // Clear all other grid-specific filters
      const reportingYear = getOmnisearchValue("reportingYear");
      const reportKind = getOmnisearchValue("reportKind");
      const workflowTags = getWorkflowTagsFromOmnisearch();

      // Create the omnisearch query string with only shared filters
      const sharedFilters = {
        ...(reportingYear && { reportingYear }),
        ...(reportKind && { reportKind }),
        // Only preserve workflow tags when navigating to workflow view
        ...(newView === "workflow" &&
          workflowTags.length > 0 && { workflowTags: workflowTags.join("|") }),
      };

      const cleanOmnisearchString = stringifyOmnisearch(sharedFilters);
      const omnisearchQuery =
        reportingYear ||
        reportKind ||
        (newView === "workflow" && workflowTags.length > 0)
          ? `?omnisearch=${cleanOmnisearchString}`
          : "";

      // Update context state and defer navigation to avoid race condition
      setOmnisearch(cleanOmnisearchString);

      // Use setTimeout to ensure navigation happens after state update
      setTimeout(() => {
        if (newView === "facilities") {
          navigate(`/staff/fulfillment/facilities${omnisearchQuery}`);
        } else if (newView === "workflow") {
          navigate(`/staff/fulfillment/workflows${omnisearchQuery}`);
        } else if (newView === "error-clustering") {
          navigate(`/staff/fulfillment/error-clustering`);
        }
      }, 0);
    },
    [navigate, getOmnisearchValue, setOmnisearch, getWorkflowTagsFromOmnisearch]
  );

  return (
    <Stack sx={{ background: blueGrey[themeMode === "light" ? 50 : 900] }}>
      {/* Toggle tabs at the top with white background */}
      {overviewMatch && (
        <Stack
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <ToggleButtonGroup
            value={currentView}
            exclusive
            onChange={handleViewChange}
            sx={{
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: 0,
                color: "text.secondary",
                pb: 1.5,
                px: 3,
                mr: 3,
                "&.Mui-selected": {
                  color: "primary.main",
                  backgroundColor: "transparent",
                  borderBottom: "3px solid",
                  borderBottomColor: "primary.main",
                },
              },
            }}
          >
            <ToggleButton value="facilities">Chemical Facilities</ToggleButton>
            <ToggleButton value="workflow">Workflow Overview</ToggleButton>
            <ToggleButton value="error-clustering">Error Triage</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      {/* Filters and Stats section with white background */}
      {overviewMatch && !errorClusteringMatch && (
        <Stack
          paddingX={3}
          paddingY={2}
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={3} alignItems="center">
              <TierIIYearPicker
                showAllOption
                persistQueryParam={false}
                value={getOmnisearchValue("reportingYear")}
                onChange={handleYearChange}
              />
              <ReportTypePicker
                reportKinds={
                  (getOmnisearchValue("reportKind")?.split("|") ??
                    []) as TierIiReportKind[]
                }
                setReportKind={setReportKind}
              />

              <WorkflowTagsButton
                selectedTags={
                  currentView === "workflow"
                    ? (() => {
                        const tags = getWorkflowTagsFromOmnisearch();
                        return tags;
                      })()
                    : []
                }
                onTagsChange={setWorkflowTags}
                disabled={currentView !== "workflow"}
              />
            </Stack>

            <Button onClick={handleToggle} variant="text">
              {statsOpen ? "Hide Stats" : "Show Stats"}
            </Button>
          </Stack>
        </Stack>
      )}
      <Outlet />
    </Stack>
  );
}

export function FulfillmentDashboard() {
  return (
    <StatsProvider>
      <OmnisearchProvider>
        <FulfillmentDashboardContent />
      </OmnisearchProvider>
    </StatsProvider>
  );
}
